import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SageIntegrationService } from '../../../shared/services/sage-integration.service';
import { ToastService } from '../../shared/services/toast.service';
import {
  aivorRecordDisplay,
  aivorRecordIdFor,
  claimedAivorRecordId,
  effectiveAivorRecordId,
  finishedGoodSyncTarget,
  fullDecisionBody,
  getEnabledEntityRoutes,
  isAivorService,
  isKeptOutOfSage,
  isSageService,
  markUnitSaved,
  matchReasonText,
  matchSageUnit,
  OverviewBundle,
  parseSageSnapshot,
  sageErrorMessageFor,
  SageEntityTypeName,
  SageReconciliationMatch,
  sageSnapshotName,
  toUnitOfMeasureOption,
  UnitOfMeasureOption,
  UpdateMatchDecisionRequest,
} from '../../../shared/services/sage-integration.model';
import {
  bundleComponentsNotReady,
  clearPassedSteps,
  DecisionCounts,
  decisionCounts,
  directionLabels,
  groupTargets,
  groupUnit,
  isExceptionAnswered,
  mergeExceptions,
  openQuestionCount,
  readPassedSteps,
  resumeIndex,
  sageUnitOf,
  StepExceptionContext,
  stepExceptions,
  stepState,
  stepSummary,
  sumDecisionCounts,
  unitChosenByHand,
  unitGroups,
  unitMatchLabel,
  WIZARD_STEP_COPY,
  WizardException,
  WizardExceptionKind,
  WizardStepCopy,
  WizardStepState,
  writePassedStep,
} from '../../../shared/services/sage-wizard.model';
import {
  readTourStatus,
  SageTourStatus,
  SageTourStep,
  wizardTourStorageKey,
  wizardTourSteps,
  writeTourStatus,
} from '../../../shared/services/sage-tour.model';
import { SageTourComponent } from './sage-tour/sage-tour.component';
import { WizardStepperComponent } from './onboarding-wizard/wizard-stepper.component';
import { WizardPairCardComponent, WizardPairChoice, WizardRecordOption } from './onboarding-wizard/wizard-pair-card.component';
import { WizardUnitCardComponent } from './onboarding-wizard/wizard-unit-card.component';
import { WizardUnitGroupCardComponent } from './onboarding-wizard/wizard-unit-group-card.component';
import { toUnitGroupView, UnitGroupView } from '../../../shared/services/sage-unit-group.model';

export type WizardScreen = 'hub' | 'step' | 'review' | 'running' | 'done';
export type RunState = 'waiting' | 'syncing' | 'done' | 'failed' | 'sent';

export interface WizardStep {
  slug: string;
  type: SageEntityTypeName;
  label: string;
  icon: string;
  accent: string;
  accentSoft: string;
  copy: WizardStepCopy;
  sessionId: number | null;
  matches: SageReconciliationMatch[];
  confirmed: boolean;
}

export interface WizardCard {
  key: string;
  kind: WizardExceptionKind;
  matchId: number;
  answered: boolean;
  sageName: string;
  sageMeta: string;
  aivorId: number | null;
  aivorName: string | null;
  aivorMeta: string;
  reason: string | null;
  choice: WizardPairChoice | null;
  code: string;
  sageUnit: string;
  itemTypeId: number;
  selectedUomId: number | null;
  isService: boolean;
}

export interface RunRow {
  slug: string;
  label: string;
  sessionId: number | null;
  state: RunState;
  message: string | null;
}

interface MatchedRow {
  key: number;
  sageName: string;
  aivorName: string | null;
}

interface AutoUnitRow {
  key: number;
  name: string;
  code: string;
  label: string;
}

interface BundleWarning {
  key: number;
  name: string;
  items: string[];
}

const REVIEW_STEP = 'review';
const MATCHED_LIST_LIMIT = 200;
// Stable empties, so a card's inputs don't change on every check while nothing has loaded.
const NO_RECORDS: WizardRecordOption[] = [];
const NO_UNITS: UnitOfMeasureOption[] = [];

@Component({
  selector: 'app-sage-overview',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    WizardStepperComponent,
    WizardPairCardComponent,
    WizardUnitCardComponent,
    WizardUnitGroupCardComponent,
    SageTourComponent,
  ],
  templateUrl: './sage-overview.component.html',
  styleUrls: ['./onboarding-wizard/sage-wizard.shared.css', './sage-help-button.shared.css', './sage-overview.component.css'],
})
export class SageOverviewComponent implements OnInit, OnDestroy {
  fullName = '';
  tenantName = '';
  private tenantKey = 'default';

  loading = true;
  loadError = false;
  loadErrorMessage = '';

  steps: WizardStep[] = [];
  stepLabels: string[] = [];
  stepStates: WizardStepState[] = [];
  stepOpen: number[] = [];
  stepCounts: DecisionCounts[] = [];
  stepDirections: string[][] = [];
  resumeAt = 0;

  screen: WizardScreen = 'hub';
  currentIndex = 0;
  stepLoading = false;
  /** Pair cards, and the unit card for an item that is alone in its unit group. */
  cards: WizardCard[] = [];
  unitGroupCards: UnitGroupView[] = [];
  openCards = 0;
  claims: ReadonlyMap<number, { matchId: number; name: string }> = new Map();
  matchedRows: MatchedRow[] = [];
  autoUnitRows: AutoUnitRow[] = [];
  bundleWarnings: BundleWarning[] = [];
  readonly matchedLimit = MATCHED_LIST_LIMIT;

  units: UnitOfMeasureOption[] | null = null;
  readonly savingIds = new Set<number>();
  private readonly autoSavingIds = new Set<number>();
  private readonly unsavedUnitIds = new Set<number>();

  reviewChecked = false;
  runRows: RunRow[] = [];
  runFailedAt: number | null = null;
  doneCounts: DecisionCounts | null = null;
  needAFix: number | null = null;

  sageCompanyName: string | null = null;
  syncEnabled = true;
  disconnecting = false;
  menuOpen = false;
  showDisconnectConfirm = false;

  tourOpen = false;
  tourSteps: SageTourStep[] = [];
  tourOfferHint = false;
  helpPulse = false;
  private tourKey: string | null = null;
  private tourShownThisVisit = false;

  private passed: Record<string, number> = {};
  private shown: WizardException[] = [];
  private readonly records = new Map<SageEntityTypeName, any[] | null>();
  private readonly recordOptions = new Map<SageEntityTypeName, WizardRecordOption[]>();
  private itemServiceOptions: WizardRecordOption[] = NO_RECORDS;
  private itemStockOptions: WizardRecordOption[] = NO_RECORDS;
  private readonly recordIds = new Map<SageEntityTypeName, ReadonlySet<number> | null>();
  private stepParam: string | null = null;
  private paramSub?: Subscription;

  constructor(
    private sageService: SageIntegrationService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    const jwt = localStorage.getItem('authToken');
    if (jwt) {
      try {
        const decoded: any = jwtDecode(jwt);
        this.fullName = decoded.FullName || '';
        this.tenantName = decoded.TenantName || '';
        this.tenantKey = String(decoded.TenantId || decoded.TenantName || 'default');
        this.tourKey = wizardTourStorageKey(decoded.TenantId, decoded.UserId);
      } catch {
        /* invalid token — leave fields blank */
      }
    }
    this.passed = readPassedSteps(this.tenantKey);

    this.paramSub = this.route.queryParamMap.subscribe((params) => {
      this.stepParam = params.get('step');
      if (!this.loading && !this.loadError) {
        this.showFromParam();
        this.syncTour();
      }
    });
    this.loadFromBundle(this.sageService.getOverviewBundle());
  }

  ngOnDestroy(): void {
    this.paramSub?.unsubscribe();
  }

  // Consumes the bundle the connect screen prefetches during its analysing animation, so a warm
  // cache renders with no fetch delay; a cold one (bookmark, back button) still resolves it here.
  private loadFromBundle(bundle$: Observable<OverviewBundle>): void {
    this.loading = true;
    this.loadError = false;
    this.loadErrorMessage = '';

    bundle$.subscribe({
      next: (bundle: OverviewBundle) => {
        if (!bundle.status.connected) {
          // Genuinely disconnected (not an API failure) — tell the connect screen why it's
          // showing up again instead of bouncing the user back with no explanation.
          this.router.navigate(['/sage-integration'], { queryParams: { sage: 'disconnected' } });
          return;
        }
        this.sageCompanyName = bundle.status.sageCompanyName?.trim() || null;
        this.syncEnabled = bundle.status.syncEnabled !== false;
        if (bundle.onboardingCompleted) {
          // The wizard is one-time: once it's complete, hand over to the console rather than
          // start fresh reconciliation sessions and offer to run the first sync again.
          this.router.navigate(['/sage-integration/console']);
          return;
        }
        if (bundle.loadError) {
          this.loading = false;
          this.loadError = true;
          this.loadErrorMessage = bundle.loadErrorMessage;
          return;
        }
        this.steps = this.buildSteps(bundle);
        this.stepLabels = this.steps.map((s) => s.label);
        const ready = () => {
          this.loading = false;
          this.showFromParam();
          this.syncTour();
        };
        // Unit questions decide where the user may go, so a link can't skip an item still needing one.
        if (this.steps.some((s) => s.type === 'Item')) {
          this.loadUnits().subscribe(ready);
        } else {
          ready();
        }
      },
      // The status check itself failed (network/backend issue) — distinct from a real
      // disconnect, so route through the existing generic error banner instead.
      error: () => this.router.navigate(['/sage-integration'], { queryParams: { sage: 'error' } }),
    });
  }

  retryLoad(): void {
    this.loadFromBundle(this.sageService.getOverviewBundle(true));
  }

  private buildSteps(bundle: OverviewBundle): WizardStep[] {
    return getEnabledEntityRoutes().map((route) => {
      const entity = (bundle.entities || []).find((e) => e.slug === route.slug);
      return {
        slug: route.slug,
        type: route.type,
        label: route.label,
        icon: route.icon,
        accent: route.accent,
        accentSoft: route.accentSoft,
        copy: WIZARD_STEP_COPY[route.type],
        sessionId: entity?.sessionId ?? null,
        matches: entity?.matches ?? [],
        confirmed: !!entity?.confirmed,
      };
    });
  }

  private loadUnits(): Observable<void> {
    return this.sageService.getUnitsOfMeasure().pipe(
      map((list) => {
        this.units = (list || []).map(toUnitOfMeasureOption);
      }),
      // Unknown units turn the unit check off, rather than flag every item as having none.
      catchError(() => of(undefined))
    );
  }

  private ctxFor(step: WizardStep): StepExceptionContext {
    return {
      entityType: step.type,
      aivorRecordIds: this.recordIds.get(step.type) ?? null,
      units: step.type === 'Item' ? this.units : null,
      unsavedUnitIds: this.unsavedUnitIds,
    };
  }

  private refreshProgress(): void {
    this.stepOpen = this.steps.map((s) => (s.confirmed ? 0 : openQuestionCount(s.matches, this.ctxFor(s))));
    // A type with no session has nothing to look at, so there is no step to pass. Units chosen by
    // hand show the step was worked through, so leaving without Next doesn't make it "Not started".
    this.stepStates = this.steps.map((s, i) => {
      const ctx = this.ctxFor(s);
      const passed = s.sessionId == null || this.passed[s.slug] === s.sessionId || s.matches.some((m) => unitChosenByHand(m, ctx));
      return stepState(s.confirmed, this.stepOpen[i], passed);
    });
    this.stepCounts = this.steps.map((s) => decisionCounts(s.matches));
    this.stepDirections = this.stepCounts.map(directionLabels);
    this.resumeAt = resumeIndex(this.stepStates);
    this.sageService.setReviewCount(this.stepOpen.reduce((sum, n) => sum + n, 0));
  }

  private showFromParam(): void {
    if (this.screen === 'running' || this.screen === 'done') return;
    this.refreshProgress();
    const param = this.stepParam;
    if (param === REVIEW_STEP) {
      if (this.resumeAt === -1) {
        this.screen = 'review';
        this.reviewChecked = false;
      } else {
        this.goToStep(this.resumeAt, true);
      }
      return;
    }
    const index = param ? this.steps.findIndex((s) => s.slug === param) : -1;
    if (index === -1) {
      this.screen = 'hub';
      if (param) this.goToHub(true);
      return;
    }
    // Categories come before the suppliers and items that use them.
    if (this.resumeAt !== -1 && index > this.resumeAt) {
      this.goToStep(this.resumeAt, true);
      return;
    }
    this.openStep(index);
  }

  goToStep(index: number, replaceUrl = false): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { step: this.steps[index].slug }, replaceUrl });
  }

  goToHub(replaceUrl = false): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: {}, replaceUrl });
  }

  goToReview(): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { step: REVIEW_STEP } });
  }

  get currentStep(): WizardStep {
    return this.steps[this.currentIndex];
  }

  get hubPrimaryLabel(): string {
    if (this.resumeAt === -1) return 'Review and finish';
    const step = this.steps[this.resumeAt];
    const started = this.stepStates.some((s) => s === 'done' || s === 'sent');
    return started ? `Continue with ${step.label}` : `Start with ${step.label}`;
  }

  startOrContinue(): void {
    if (this.resumeAt === -1) {
      this.goToReview();
    } else {
      this.goToStep(this.resumeAt);
    }
  }

  canOpen(index: number): boolean {
    return !this.steps[index].confirmed && (this.resumeAt === -1 || index <= this.resumeAt);
  }

  statusLabel(index: number): string {
    switch (this.stepStates[index]) {
      case 'sent':
        return 'Sent to Sage';
      case 'needs':
        return `Needs you (${this.stepOpen[index]})`;
      case 'done':
        return 'Done';
      default:
        return 'Not started';
    }
  }

  private openStep(index: number): void {
    this.screen = 'step';
    this.currentIndex = index;
    this.shown = [];
    this.cards = [];
    this.unitGroupCards = [];
    this.openCards = 0;
    const step = this.steps[index];

    const types: SageEntityTypeName[] = step.type === 'ItemBundle' ? ['ItemBundle', 'Item'] : [step.type];
    const loads: Observable<unknown>[] = types
      .filter((t) => !this.records.has(t))
      .map((t) =>
        this.sageService.getAivorRecords(t).pipe(
          catchError(() => of(null)),
          map((list) => this.storeRecords(t, list))
        )
      );
    if (step.type === 'Item' && this.units === null) loads.push(this.loadUnits());

    if (!loads.length) {
      this.prepareStep();
      return;
    }
    this.stepLoading = true;
    forkJoin(loads).subscribe(() => {
      this.stepLoading = false;
      if (this.screen === 'step' && this.currentIndex === index) this.prepareStep();
    });
  }

  private storeRecords(type: SageEntityTypeName, list: any[] | null): void {
    this.records.set(type, list);
    const options = (list || []).map((r) => ({ id: aivorRecordIdFor(type, r) ?? 0, ...aivorRecordDisplay(type, r) }));
    this.recordOptions.set(type, options);
    this.recordIds.set(type, list ? new Set(options.map((o) => o.id)) : null);
    if (type === 'Item') {
      const services = (list || []).map(isAivorService);
      const keptOut = (list || []).map(isKeptOutOfSage);
      this.itemServiceOptions = options.filter((_, i) => services[i]);
      this.itemStockOptions = options.filter((_, i) => !services[i] && !keptOut[i]);
    }
  }

  private prepareStep(): void {
    this.shown = stepExceptions(this.currentStep.matches, this.ctxFor(this.currentStep));
    this.rebuildCards();
  }

  private rebuildCards(): void {
    const step = this.currentStep;
    if (step.type === 'Item') this.saveEquivalentUnits(step);
    const ctx = this.ctxFor(step);
    this.shown = mergeExceptions(this.shown, stepExceptions(step.matches, ctx));
    const byId = new Map(step.matches.map((m) => [m.id, m]));
    this.claims = this.claimsFor(step);
    const shown = this.shown.filter((e) => byId.has(e.matchId));
    const unitMatches = shown.filter((e) => e.kind === 'unit').map((e) => byId.get(e.matchId)!);
    const groups = unitGroups(unitMatches, this.units ?? NO_UNITS);
    // An item alone in its group keeps the plain unit card; a suggestion always needs its Use button.
    const single = new Set(groups.filter((g) => g.matches.length === 1 && g.kind !== 'suggested').map((g) => g.matches[0].id));
    this.cards = shown
      .filter((e) => e.kind !== 'unit' || single.has(e.matchId))
      .map((e) => this.toCard(e, byId.get(e.matchId)!, step, ctx));
    this.unitGroupCards = groups.filter((g) => !single.has(g.matches[0].id)).map((g) => toUnitGroupView(g, this.units ?? NO_UNITS));
    this.openCards = this.cards.filter((c) => !c.answered).length + this.unitGroupCards.filter((g) => !g.answered).length;
    this.matchedRows = this.buildMatchedRows(step);
    this.autoUnitRows = step.type === 'Item' ? this.buildAutoUnitRows(step) : [];
    this.bundleWarnings = step.type === 'ItemBundle' ? this.buildBundleWarnings(step) : [];
    this.refreshProgress();
  }

  private claimsFor(step: WizardStep): Map<number, { matchId: number; name: string }> {
    const claims = new Map<number, { matchId: number; name: string }>();
    for (const m of step.matches) {
      const aivorId = claimedAivorRecordId(m);
      if (aivorId != null && !claims.has(aivorId)) {
        claims.set(aivorId, { matchId: m.id, name: sageSnapshotName(m.sageRecordSnapshot, step.type) });
      }
    }
    return claims;
  }

  /** On the Item step a Sage service can only be paired with an Aivor service, and a stock item
   * only with a stock item. */
  recordOptionsFor(type: SageEntityTypeName, sageIsService = false): WizardRecordOption[] {
    if (type === 'Item') return sageIsService ? this.itemServiceOptions : this.itemStockOptions;
    return this.recordOptions.get(type) ?? NO_RECORDS;
  }

  get unitOptions(): UnitOfMeasureOption[] {
    return this.units ?? NO_UNITS;
  }

  private recordName(type: SageEntityTypeName, id: number | null): WizardRecordOption | undefined {
    return id == null ? undefined : this.recordOptions.get(type)?.find((o) => o.id === id);
  }

  private toCard(e: WizardException, m: SageReconciliationMatch, step: WizardStep, ctx: StepExceptionContext): WizardCard {
    const raw = parseSageSnapshot(m.sageRecordSnapshot);
    // A pair keeps showing the suggested record after "No, different", so the card doesn't jump.
    const aivorId = e.kind === 'pair' ? (m.resolvedAivorRecordId ?? m.aivorRecordId ?? null) : effectiveAivorRecordId(m);
    const record = this.recordName(step.type, aivorId);
    const sageMeta =
      step.type === 'ItemBundle' ? raw?.BundleCode ?? '' : [raw?.Code, raw?.Email].filter(Boolean).join(' · ');
    return {
      key: `${e.kind}:${m.id}`,
      kind: e.kind,
      matchId: m.id,
      answered: isExceptionAnswered(e, m, ctx),
      sageName: sageSnapshotName(m.sageRecordSnapshot, step.type),
      sageMeta,
      aivorId,
      aivorName: record?.name ?? (e.kind === 'pair' && aivorId != null ? `Aivor record #${aivorId}` : null),
      aivorMeta: record?.meta ?? '',
      reason: e.kind === 'new-to-aivor' ? matchReasonText(m) : null,
      choice: this.choiceFor(e.kind, m, ctx),
      code: raw?.Code ?? '',
      sageUnit: sageUnitOf(m),
      itemTypeId: m.resolvedItemTypeID ?? 1,
      selectedUomId: m.resolvedUOMID ?? null,
      isService: step.type === 'Item' && isSageService(raw),
    };
  }

  private choiceFor(kind: WizardExceptionKind, m: SageReconciliationMatch, ctx: StepExceptionContext): WizardPairChoice | null {
    const linkedTo = effectiveAivorRecordId(m);
    if (m.decision === 'ImportToAivor') return kind === 'pair' ? 'different' : 'add';
    if (m.decision !== 'Confirmed') return null;
    switch (kind) {
      case 'pair':
        return linkedTo === m.aivorRecordId ? 'same' : 'picked';
      case 'new-to-aivor':
        return linkedTo ? 'picked' : 'leave-out';
      case 'missing-aivor':
        return linkedTo && ctx.aivorRecordIds?.has(linkedTo) ? 'picked' : null;
      default:
        return null;
    }
  }

  private buildMatchedRows(step: WizardStep): MatchedRow[] {
    const asked = new Set(this.shown.map((e) => e.matchId));
    return step.matches
      .filter((m) => m.decision === 'Confirmed' && !!m.sageRecordId && !!effectiveAivorRecordId(m) && !asked.has(m.id))
      .map((m) => {
        const sageName = sageSnapshotName(m.sageRecordSnapshot, step.type);
        const aivorName = this.recordName(step.type, effectiveAivorRecordId(m))?.name ?? null;
        const same = !aivorName || aivorName.trim().toLowerCase() === sageName.trim().toLowerCase();
        return { key: m.id, sageName, aivorName: same ? null : aivorName };
      });
  }

  private buildAutoUnitRows(step: WizardStep): AutoUnitRow[] {
    if (!this.units) return [];
    const unitCards = new Set(this.shown.filter((e) => e.kind === 'unit').map((e) => e.matchId));
    const rows: AutoUnitRow[] = [];
    for (const m of step.matches) {
      if (m.decision !== 'ImportToAivor' || unitCards.has(m.id)) continue;
      const sageUnit = sageUnitOf(m);
      const option = m.resolvedUOMID ? this.units.find((u) => u.id === m.resolvedUOMID) : matchSageUnit(sageUnit, this.units)?.option;
      if (!option) continue;
      const raw = parseSageSnapshot(m.sageRecordSnapshot);
      rows.push({
        key: m.id,
        name: sageSnapshotName(m.sageRecordSnapshot, step.type),
        code: raw?.Code ?? '',
        label: unitMatchLabel(sageUnit || option.code, option),
      });
    }
    return rows;
  }

  private buildBundleWarnings(step: WizardStep): BundleWarning[] {
    const items = this.records.get('Item') || [];
    const bundles = this.records.get('ItemBundle') || [];
    const itemMatches = this.steps.find((s) => s.type === 'Item')?.matches ?? [];
    const warnings: BundleWarning[] = [];
    for (const m of step.matches) {
      const sent = (m.decision === 'Confirmed' && !!m.sageRecordId) || (m.decision === 'CreateInSage' && !this.claims.has(m.aivorRecordId ?? -1));
      if (!sent) continue;
      const aivorId = m.decision === 'CreateInSage' ? m.aivorRecordId : effectiveAivorRecordId(m);
      const bundle = bundles.find((b) => aivorRecordIdFor('ItemBundle', b) === aivorId);
      // A standalone finished good goes to Sage as one item, without its components.
      if (!bundle || finishedGoodSyncTarget(bundle) === 'Standalone') continue;
      const notReady = bundleComponentsNotReady(bundle.bundleInfo?.componentItemIds ?? [], itemMatches, items);
      if (!notReady.length) continue;
      warnings.push({
        key: m.id,
        name: aivorRecordDisplay('ItemBundle', bundle).name,
        items: notReady.map((id) => this.recordName('Item', id)?.name ?? `Item #${id}`),
      });
    }
    return warnings;
  }

  /** Saves an equivalent unit (m2 for m²) as the user's choice, so the step lists it under "Units
   * matched automatically"; one that won't save becomes a question instead. */
  private saveEquivalentUnits(step: WizardStep): void {
    if (!this.units || !step.sessionId) return;
    const byUnit = new Map<number, SageReconciliationMatch[]>();
    for (const m of step.matches) {
      if (m.decision !== 'ImportToAivor' || m.resolvedUOMID || this.autoSavingIds.has(m.id) || this.unsavedUnitIds.has(m.id)) continue;
      const unitMatch = matchSageUnit(sageUnitOf(m), this.units);
      if (unitMatch?.kind !== 'equivalent') continue;
      byUnit.set(unitMatch.option.id, [...(byUnit.get(unitMatch.option.id) ?? []), m]);
    }
    for (const [uomId, matches] of byUnit) {
      const ids = matches.map((m) => m.id);
      ids.forEach((id) => this.autoSavingIds.add(id));
      const settle = (saved: boolean) => {
        for (const m of matches) {
          this.autoSavingIds.delete(m.id);
          if (saved) markUnitSaved(m, uomId);
          else this.unsavedUnitIds.add(m.id);
        }
        if (this.screen === 'step' && this.currentStep === step) this.rebuildCards();
      };
      this.sageService.resolveUoms(step.sessionId, ids, uomId).subscribe({
        next: () => settle(true),
        error: () => settle(false),
      });
    }
  }

  get busy(): boolean {
    return this.stepLoading || this.savingIds.size > 0 || this.autoSavingIds.size > 0;
  }

  get canGoNext(): boolean {
    return !this.busy && this.openCards === 0;
  }

  get nextLabel(): string {
    const next = this.steps[this.currentIndex + 1];
    return next ? `Next: ${next.label}` : 'Review and finish';
  }

  private matchOf(matchId: number): SageReconciliationMatch | undefined {
    return this.currentStep.matches.find((m) => m.id === matchId);
  }

  answer(card: WizardCard, choice: WizardPairChoice): void {
    switch (choice) {
      case 'same':
        this.saveDecision(card.matchId, { decision: 'Confirmed', resolvedAivorRecordId: card.aivorId });
        break;
      case 'different':
      case 'add':
        this.saveDecision(card.matchId, { decision: 'ImportToAivor', resolvedAivorRecordId: null });
        break;
      case 'leave-out':
        // Confirmed with nothing linked creates nothing, as the match table's Confirm does.
        this.saveDecision(card.matchId, { decision: 'Confirmed', resolvedAivorRecordId: null });
        break;
    }
  }

  pickRecord(card: WizardCard, aivorId: number): void {
    this.saveDecision(card.matchId, { decision: 'Confirmed', resolvedAivorRecordId: aivorId });
  }

  setItemType(card: { matchId: number }, itemTypeId: number): void {
    this.saveDecision(card.matchId, { resolvedItemTypeID: itemTypeId });
  }

  private saveDecision(matchId: number, changes: Partial<UpdateMatchDecisionRequest>): void {
    const step = this.currentStep;
    const match = this.matchOf(matchId);
    if (!step.sessionId || !match || this.savingIds.has(matchId)) return;
    const body = fullDecisionBody(match, changes);
    this.savingIds.add(matchId);
    this.sageService.updateMatchDecision(step.sessionId, matchId, body).subscribe({
      next: () => {
        this.savingIds.delete(matchId);
        Object.assign(match, body);
        this.rebuildCards();
      },
      error: () => {
        this.savingIds.delete(matchId);
        this.toast.error('Could not save your answer. Please try again.');
      },
    });
  }

  chooseUnit(card: { matchId: number }, uomId: number): void {
    const step = this.currentStep;
    const match = this.matchOf(card.matchId);
    if (!step.sessionId || !match || this.savingIds.has(card.matchId)) return;
    this.savingIds.add(card.matchId);
    this.sageService.resolveUom(step.sessionId, card.matchId, { purchaseUOMID: uomId }).subscribe({
      next: () => {
        this.savingIds.delete(card.matchId);
        markUnitSaved(match, uomId);
        this.unsavedUnitIds.delete(card.matchId);
        this.rebuildCards();
      },
      error: () => {
        this.savingIds.delete(card.matchId);
        this.toast.error('Could not save the unit. Please try again.');
      },
    });
  }

  chooseGroupUnit(group: UnitGroupView, uomId: number): void {
    const step = this.currentStep;
    const members = group.items.map((i) => this.matchOf(i.matchId)).filter((m): m is SageReconciliationMatch => !!m);
    if (!step.sessionId || this.groupSaving(group)) return;
    // A card answered "leave it out" can leave an item in its group; the server refuses non-imports.
    const targets = groupTargets(members, groupUnit(members)).filter(
      (m) => m.resolvedUOMID !== uomId && (m.decision === 'ImportToAivor' || m.decision === 'NeedsUomMapping')
    );
    if (!targets.length) return;
    const ids = targets.map((m) => m.id);
    ids.forEach((id) => this.savingIds.add(id));
    this.sageService.resolveUoms(step.sessionId, ids, uomId).subscribe({
      next: () => {
        ids.forEach((id) => this.savingIds.delete(id));
        for (const m of targets) {
          markUnitSaved(m, uomId);
          this.unsavedUnitIds.delete(m.id);
        }
        this.rebuildCards();
      },
      error: () => {
        ids.forEach((id) => this.savingIds.delete(id));
        this.toast.error('Could not save the unit. Please try again.');
      },
    });
  }

  groupSaving(group: UnitGroupView): boolean {
    return group.items.some((i) => this.savingIds.has(i.matchId));
  }

  next(): void {
    if (!this.canGoNext) return;
    const step = this.currentStep;
    if (step.sessionId != null) {
      // Kept in memory too: blocked storage must not hold the user on this step.
      this.passed = { ...this.passed, [step.slug]: step.sessionId };
      writePassedStep(this.tenantKey, step.slug, step.sessionId);
    }
    this.refreshProgress();
    if (this.currentIndex < this.steps.length - 1) {
      this.goToStep(this.currentIndex + 1);
    } else {
      this.goToReview();
    }
  }

  back(): void {
    if (this.currentIndex > 0) {
      this.goToStep(this.currentIndex - 1);
    } else {
      this.goToHub();
    }
  }

  reviewEveryRecord(): void {
    // The match table edits these sessions too, so reload them on return.
    this.sageService.clearOverviewBundle();
    this.router.navigate(['/sage-integration', this.currentStep.slug]);
  }

  goToItems(): void {
    const index = this.steps.findIndex((s) => s.type === 'Item');
    if (index !== -1) this.goToStep(index);
  }

  get stepSummaryText(): string {
    const step = this.currentStep;
    if (step.confirmed) return `These ${step.copy.nouns} were sent to Sage in an earlier run.`;
    return stepSummary(step.copy, this.stepCounts[this.currentIndex], this.openCards);
  }

  get reviewCounts(): DecisionCounts {
    return sumDecisionCounts(this.stepCounts);
  }

  backFromReview(): void {
    this.goToStep(this.steps.length - 1);
  }

  startRun(): void {
    if (!this.reviewChecked || this.screen !== 'review') return;
    this.doneCounts = this.reviewCounts;
    this.runRows = this.steps.map((s) => ({
      slug: s.slug,
      label: s.label,
      sessionId: s.sessionId,
      state: s.confirmed ? 'sent' : 'waiting',
      message: null,
    }));
    this.screen = 'running';
    this.runFrom(0);
  }

  // In step order, as suppliers need their categories in Sage and bundles their items. A failed
  // confirm leaves its session open, so the run stops there and Try again confirms it again.
  private runFrom(start: number): void {
    const index = this.runRows.findIndex((r, i) => i >= start && (r.state === 'waiting' || r.state === 'failed'));
    if (index === -1) {
      this.finishRun();
      return;
    }
    const row = this.runRows[index];
    if (row.sessionId == null) {
      row.state = 'done';
      this.runFrom(index + 1);
      return;
    }
    row.state = 'syncing';
    row.message = null;
    this.runFailedAt = null;
    this.sageService.confirmSession(row.sessionId).subscribe({
      next: () => {
        row.state = 'done';
        this.runFrom(index + 1);
      },
      error: (err) => {
        row.state = 'failed';
        row.message = sageErrorMessageFor(err) ?? 'Something went wrong while sending this to Sage.';
        this.runFailedAt = index;
      },
    });
  }

  retryRun(): void {
    if (this.runFailedAt === null) return;
    this.runFrom(this.runFailedAt);
  }

  get runFailedLabel(): string | null {
    return this.runFailedAt === null ? null : this.runRows[this.runFailedAt].label;
  }

  runStateLabel(row: RunRow): string {
    switch (row.state) {
      case 'syncing':
        return 'Syncing…';
      case 'done':
        return 'Done';
      case 'failed':
        return 'Stopped';
      case 'sent':
        return 'Already sent';
      default:
        return 'Waiting';
    }
  }

  private finishRun(): void {
    // Done either way: the backend also works completion out from the confirmed sessions, so a
    // failed stamp heals itself and must not strand the user in the wizard.
    this.sageService.completeOnboarding().subscribe({
      next: () => this.showDone(),
      error: () => this.showDone(),
    });
  }

  private showDone(): void {
    this.sageService.clearOverviewBundle();
    clearPassedSteps(this.tenantKey);
    this.screen = 'done';
    this.needAFix = null;
    this.sageService.getActionableErrors().subscribe({
      next: (page) => (this.needAFix = page?.totalCount ?? 0),
      error: () => (this.needAFix = null),
    });
  }

  goToConsole(): void {
    this.router.navigate(['/sage-integration/console']);
  }

  openFixes(): void {
    this.router.navigate(['/sage-integration/console'], { queryParams: { tab: 'errors' } });
  }

  openFixMatches(): void {
    this.router.navigate(['/sage-integration/console'], { queryParams: { tab: 'matches' } });
  }

  // Moving off the hub ends it unsaved, as leaving the console mid-tour does.
  private syncTour(): void {
    if (this.screen !== 'hub' || this.loading || this.loadError) {
      if (this.tourOpen) this.onTourClosed();
      return;
    }
    if (this.tourOpen || this.tourShownThisVisit) return;
    if (!this.tourKey || readTourStatus(this.tourKey) !== null) return;
    this.openTour();
  }

  openTour(): void {
    this.tourSteps = wizardTourSteps(this.steps);
    this.tourOfferHint = !!this.tourKey && readTourStatus(this.tourKey) === null;
    this.tourShownThisVisit = true;
    this.helpPulse = false;
    this.menuOpen = false;
    this.tourOpen = true;
  }

  onTourOutcome(status: SageTourStatus): void {
    writeTourStatus(this.tourKey, status);
    this.helpPulse = this.tourOfferHint;
  }

  onTourClosed(): void {
    this.tourOpen = false;
    this.helpPulse = false;
  }

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.menuOpen) return;
    const target = event.target as HTMLElement | null;
    const btn = document.getElementById('overviewMenuBtn');
    const menu = document.getElementById('overviewMenu');
    if (target && (btn?.contains(target) || menu?.contains(target))) {
      return;
    }
    this.menuOpen = false;
  }

  disconnectSage(): void {
    this.menuOpen = false;
    if (this.disconnecting) return;
    this.showDisconnectConfirm = true;
  }

  cancelDisconnect(): void {
    this.showDisconnectConfirm = false;
  }

  confirmDisconnect(): void {
    this.showDisconnectConfirm = false;
    this.disconnecting = true;
    this.sageService.disconnect().subscribe({
      next: () => {
        this.sageService.clearOverviewBundle();
        this.router.navigate(['/sage-integration']);
      },
      error: () => {
        this.disconnecting = false;
        this.toast.error('Could not disconnect Sage. Please try again.');
      },
    });
  }

  trackByKey(_: number, item: { key: string | number }): string | number {
    return item.key;
  }

  trackBySlug(_: number, item: { slug: string }): string {
    return item.slug;
  }
}
